import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import { SPACE_DEFAULT_TITLE } from '@gitbook/app-database';
import { SearchModalResults, type SearchResultGroupInfo } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Description, EmptyState } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { useNavigation } from '../../../navigation';
import { SpaceIcon } from '../..';
import {
    type AskGitBookSearchSuggestion,
    type AskGitBookSearchSuggestionGroup,
    useAskGitBookSuggestion,
} from '../../AISearch';
import {
    type ContentSearchGroupSpace,
    type ContentSearchResult,
    useContentSearchResults,
} from '../../Search';

/**
 * For a non-empty search query, fetch the results and display them.
 */
export function AppSearchResults(props: {
    query: string;
    onSubmitAIQuery: (newQuery: string, trigger?: 'click_ask_gitbook' | 'press_enter') => void;
    organization: string | null;
    hasAI?: boolean;
}): React.ReactElement {
    const { onSubmitAIQuery, hasAI, organization } = props;
    const query = React.useDeferredValue(props.query);

    const { navigate } = useNavigation();
    const askGitBook = useAskGitBookSuggestion(query, onSubmitAIQuery, hasAI);
    const searchResults = useContentSearchResults({ organizationId: organization, query });

    const results = askGitBook.suggestion
        ? [askGitBook.suggestion, ...searchResults]
        : searchResults;

    const renderItem = React.useCallback(
        (item: ContentSearchResult | AskGitBookSearchSuggestion) => {
            if (item.type === 'ask-gitbook') {
                return askGitBook.onRenderSuggestion(item);
            }
            switch (item.type) {
                case 'space': {
                    return {
                        title: item.title,
                        icon: <SpaceIcon space={item.space} />,
                        action: 'Go to space',
                    };
                }
                case 'page':
                    return {
                        title: item.title,
                        icon: Icon.Page,
                        action: 'Go to page',
                    };
                case 'section':
                    return {
                        title: item.title,
                        body: item.body,
                        icon: Icon.ArrowCurveDownRight,
                        nested: true,
                        action: 'Go to section',
                    };
                default:
                    assertNever(item);
            }
        },
        [query, askGitBook.onRenderSuggestion]
    );

    const renderGroup = React.useCallback(
        (group: ContentSearchGroupSpace | undefined): SearchResultGroupInfo | undefined => {
            if (typeof group === 'string') {
                return {
                    icon: Icon.Space,
                    title: group,
                };
            }

            if (group?.type === 'space') {
                return {
                    icon: <SpaceIcon space={group.space} />,
                    title: group.space.title || SPACE_DEFAULT_TITLE,
                };
            }

            return undefined;
        },
        []
    );

    const onOpen = React.useCallback(
        (item: ContentSearchResult | AskGitBookSearchSuggestion, index: number) => {
            if (item.type === 'ask-gitbook') {
                return askGitBook.onOpenSuggestion(item);
            }

            analytics.logEvent({
                type: 'search_open_result',
                resultType: item.type,
                resultIndex: index,
            });

            switch (item.type) {
                case 'space': {
                    navigate({
                        screen: {
                            type: 'space',
                            space: item.space.id,
                            organization: item.space.organization,
                            page: '',
                            query: undefined,
                        },
                    });
                    break;
                }
                case 'page':
                    navigate({
                        screen: {
                            type: 'space',
                            space: item.space.id,
                            organization: item.space.organization,
                            page: item.path,
                            query: undefined,
                        },
                    });
                    break;
                case 'section':
                    navigate({
                        screen: {
                            type: 'space',
                            space: item.space.id,
                            organization: item.space.organization,
                            page: item.path,
                            hash: item.anchor,
                            query: undefined,
                        },
                    });
                    break;
                default:
                    assertNever(item);
            }
        },
        [askGitBook.onOpenSuggestion, navigate]
    );

    return (
        <>
            <SearchModalResults
                data={results}
                getKey={getKey}
                renderItem={renderItem}
                renderGroup={renderGroup}
                groupItem={groupSearchItem}
                getGroupKey={getGroupKey}
                onOpen={onOpen}
                className="px-4 pt-2 pb-4"
            />
            {searchResults.length === 0 ? (
                <EmptyState>
                    <Description>No search results for "{query}"</Description>
                </EmptyState>
            ) : null}
        </>
    );
}

function getKey(item: ContentSearchResult): string {
    return item.key;
}

function getGroupKey(
    group: ContentSearchGroupSpace | AskGitBookSearchSuggestion | string | undefined
): string {
    if (typeof group === 'string') {
        return group;
    }

    if (!group) {
        return '';
    }

    if (group.type === 'ask-gitbook') {
        return group.type;
    }

    return group.space.id;
}

function groupSearchItem(
    item: ContentSearchResult | AskGitBookSearchSuggestion
): ContentSearchGroupSpace | AskGitBookSearchSuggestionGroup | string | undefined {
    if (item.type === 'ask-gitbook') {
        return {
            type: 'ask-gitbook',
        };
    }

    if (item.type === 'space') {
        return 'Spaces';
    }

    if (!item.groups) {
        return;
    }

    return item.groups.find((group) => group.type === 'space') as ContentSearchGroupSpace;
}
